/**
 * Suite API client -- thin wrappers around the dm-backend suite endpoints.
 *
 * Routes are exposed at BOTH /api/* (engine/internal-token callbacks) AND
 * /dm-api/* (the browser-facing SPA path via nginx). The SPA always uses the
 * /dm-api path through the shared dmClient axios instance.
 *
 * Endpoint families:
 *   - eligibility/auth         -> Auth & Eligibility module
 *   - medical-necessity        -> Medical Necessity module
 *   - scrubber                 -> Claim Right + Coding Intelligence modules
 *   - appeals/build            -> Appeal Builder module
 *   - analytics/*              -> Payer Intelligence module
 *   - claims/denials passthrough -> Denial Resolve module (existing endpoints)
 */
import { dmClient } from './dmClient';

// ---------------------------------------------------------------------------
// Auth & Eligibility
// ---------------------------------------------------------------------------

export async function verifyEligibility(body) {
  const { data } = await dmClient.post('/eligibility/verify', body);
  return data;
}

export async function submitPriorAuth(body) {
  const { data } = await dmClient.post('/auth/submit', body);
  return data;
}

export async function getPriorAuthStatus(authNumber, params = {}) {
  const { data } = await dmClient.get(`/auth/status/${authNumber}`, { params });
  return data;
}

export async function listPriorAuths(params = {}) {
  const { data } = await dmClient.get('/auth/list', { params });
  return data;
}

// ---------------------------------------------------------------------------
// Medical Necessity
// ---------------------------------------------------------------------------

export async function checkMedicalNecessity(body) {
  const { data } = await dmClient.post('/medical-necessity/check', body);
  return data;
}

export async function checkMedicalNecessityV5(body) {
  const { data } = await dmClient.post('/medical-necessity/check-v5', body);
  return data;
}

export async function fetchMedicalNecessityCriteria(body) {
  const { data } = await dmClient.post('/medical-necessity/criteria', body);
  return data;
}

// ---------------------------------------------------------------------------
// Scrubber (Claim Right + Coding Intelligence)
// ---------------------------------------------------------------------------

export async function scrubCodes(body) {
  const { data } = await dmClient.post('/scrubber/codes', body);
  return data;
}

export async function scrubClaim(body) {
  const { data } = await dmClient.post('/scrubber/claim', body);
  return data;
}

// ---------------------------------------------------------------------------
// Documentation gap analyzer (Gap 4)
// ---------------------------------------------------------------------------

export async function analyzeDocumentationGap(body) {
  const { data } = await dmClient.post('/documentation/gap', body);
  return data;
}

// ---------------------------------------------------------------------------
// Coordination of benefits (Gap 7)
// ---------------------------------------------------------------------------

export async function coordinateBenefits(body) {
  const { data } = await dmClient.post('/eligibility/cob', body);
  return data;
}

// ---------------------------------------------------------------------------
// Appeal Builder
// ---------------------------------------------------------------------------

export async function buildAppealPacket(body) {
  const { data } = await dmClient.post('/appeals/build', body);
  return data;
}

// ---------------------------------------------------------------------------
// Appeal deadline calendar (Gap 5)
// ---------------------------------------------------------------------------

export async function fetchAppealCalendar(params = {}) {
  const { data } = await dmClient.get('/appeals/calendar', { params });
  return data;
}

export async function recomputeDeadlines(body) {
  const { data } = await dmClient.post('/appeals/deadlines/recompute', body);
  return data;
}

export async function markDeadlineFiled(deadlineId) {
  const { data } = await dmClient.post(`/appeals/deadlines/${deadlineId}/mark-filed`);
  return data;
}

// ---------------------------------------------------------------------------
// Appeal predictor (Gap 9)
// ---------------------------------------------------------------------------

export async function fetchAppealPrediction(body) {
  const { data } = await dmClient.post('/appeals/predict', body);
  return data;
}

// ---------------------------------------------------------------------------
// Payer Intelligence (analytics)
// ---------------------------------------------------------------------------

export async function getAnalyticsSummary() {
  const { data } = await dmClient.get('/analytics/summary');
  return data;
}

export async function getDenialsByPayer() {
  const { data } = await dmClient.get('/analytics/denials-by-payer');
  return data;
}

export async function getDenialsByCarc() {
  const { data } = await dmClient.get('/analytics/by-carc');
  return data;
}

export async function getTurnaround() {
  const { data } = await dmClient.get('/analytics/turnaround');
  return data;
}

export async function getUnderpayment() {
  const { data } = await dmClient.get('/analytics/underpayment');
  return data;
}

export async function getLearningSignals() {
  const { data } = await dmClient.get('/analytics/learning-signals');
  return data;
}

// Gap 8: contract reconciliation roll-up.
export async function getContractVariance(params = {}) {
  const { data } = await dmClient.get('/analytics/contract-variance', { params });
  return data;
}

// Gap 10: DRG validation + HCC capture review.
export async function getDrgHccReview(params = {}) {
  const { data } = await dmClient.get('/analytics/drg-hcc', { params });
  return data;
}

// ---------------------------------------------------------------------------
// Denial Resolve (existing endpoints -- re-exported here so suite pages can
// import everything from one place).
// ---------------------------------------------------------------------------

export async function listDenials(params = {}) {
  const { data } = await dmClient.get('/denials', { params });
  return data;
}

export async function getDenialsSummary() {
  const { data } = await dmClient.get('/denials/summary');
  return data;
}

export async function listClaims(params = {}) {
  const { data } = await dmClient.get('/claims', { params });
  return data;
}

export async function getClaimsSummary() {
  const { data } = await dmClient.get('/claims/summary');
  return data;
}

export async function getClaim(claimId) {
  const { data } = await dmClient.get(`/claims/${claimId}`);
  return data;
}

export async function listPayers() {
  const { data } = await dmClient.get('/claims/payers');
  return data;
}

export async function upsertPayer(body) {
  const { data } = await dmClient.post('/claims/payers', body);
  return data;
}
